import React from 'react';
import { Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import FiltersCount from '../../search/components/Filters/HorizontalFilters/Pill/FiltersCount';
import { IconFilters, IconSorting } from '../common/icons';
import { useCurrentFilterStatus } from '../CurrentFilterStatusProvider';
import { isAnyFilterSelected } from './ChangeTransportFiltersProvider';

// #region styled components

const MenuTabContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  text-align: center;
  padding: 12px;
  font-weight: 700;
  line-height: 16px;
  font-size: 20px;
  cursor: pointer;
  gap: 8px;
`;

const IconPlaceholder = styled.div`
  width: 20px;
  height: 20px;
`;

// #endregion

type ChangeTransportSubheaderMenuType = {
  isTrain: boolean;
  loading?: boolean;
  openFilterModal: () => void;
  openSortModal: () => void;
};

export function ChangeTransportSubheaderMenu(props: ChangeTransportSubheaderMenuType) {
  const { isTrain, openSortModal, openFilterModal, loading = false } = props;
  const { t } = useTranslation();
  const { currentFiltersStatus = undefined } = useCurrentFilterStatus();

  return (
    <div
      css={css`
        display: flex;
        position: fixed;
        bottom: 16px;
        left: 0px;
        width: 100%;
        z-index: 1;
        justify-content: center;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundNeutralContrast};
          color: ${({ theme }) => theme.kallisto.color.icon.static.iconOnDark};
          box-shadow:
            0 0 20px rgba(0, 0, 0, 0.04),
            0 0 10px rgba(0, 0, 0, 0.04),
            0 0 4px rgba(0, 0, 0, 0.02);
          border-radius: 24px;
          padding: 0 4px;
        `}
      >
        <MenuTabContainer
          role="button"
          aria-label="sort flights options"
          onClick={() => !loading && openSortModal()}
          onKeyDown={() => !loading && openSortModal()}
          css={css`
            position: relative;
            &:after {
              content: '';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              height: 50%;
              border-right: 1px solid ${(p) => p.theme.kallisto.color.border.interactive.borderNeutralDefault};
              width: 1px;
            }
          `}
        >
          {loading ? <IconPlaceholder /> : <IconSorting />}
          <Text variant="BodySmall" weight="bold" color="textOnDark">
            {t(isTrain ? 'vacation.trainavailability.sort.title' : 'vacation.flightavailability.sort.title')}
          </Text>
        </MenuTabContainer>
        <MenuTabContainer
          role="button"
          aria-label="filter flights options"
          onClick={() => !loading && openFilterModal()}
          onKeyDown={() => !loading && openFilterModal()}
        >
          {loading ? <IconPlaceholder /> : <IconFilterWithDot withDot={isAnyFilterSelected(currentFiltersStatus)} />}
          <Text variant="BodySmall" weight="bold" color="textOnDark">
            {t('vacation.common.filter')}
          </Text>
        </MenuTabContainer>
      </div>
    </div>
  );
}

function IconFilterWithDot(props: { withDot: boolean }) {
  const { withDot } = props;
  const fixedPosition = withDot
    ? css`
        [class*='FiltersCountContainer'] {
          top: 10px;
          left: 40px;
        }
      `
    : null;

  return (
    <div
      css={`
        ${fixedPosition}
      `}
    >
      <IconFilters />
      {withDot && <FiltersCount withNumber={false} isAllFilters={false} showOnRight={true} />}
    </div>
  );
}
